import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Spinner,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import {
  ArrowDownload20Regular,
  Delete20Regular,
  Open20Regular,
} from '@fluentui/react-icons';
import { Link } from 'react-router-dom';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  getStorageStatus,
  listStorageFiles,
  deleteStorageExport,
  deleteQuoteExcel,
  exportDownloadUrl,
  formatBytes,
  type StorageFile,
  type StorageFileType,
  type StorageStatusResponse,
} from '../api/storage.js';
import { deleteFile as deleteProjectFile } from '../api/projects.js';
import { deleteCustomDb } from '../api/customDbs.js';
import { deleteCatalog } from '../api/catalogs.js';
import { descargarParaAdjuntar } from '../api/mensajes.js';
import { BotonesMensajes } from '../components/mensajes/BotonesMensajes.js';
import { mensajeDelPresupuesto, mensajeDelResumen } from '../lib/enviarMensaje.js';
import { brandingProductoActivo } from '../lib/brandingDelProducto.js';

const useStyles = makeStyles({
  page: {
    maxWidth: '1080px',
    margin: '0 auto',
    padding: 'var(--space-xl) var(--space-xxl) var(--space-xxxl)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-l)',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--fs-600)',
    fontWeight: 700,
    margin: 0,
  },
  subtitle: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--fs-300)',
    margin: 0,
  },
  usageCard: {
    background: 'var(--bg-raised)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-l) var(--space-xl)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-s)',
  },
  usageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 'var(--space-m)',
  },
  usageValue: {
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: 'var(--fs-500)',
  },
  usageQuota: {
    color: 'var(--text-tertiary)',
    fontWeight: 400,
    fontSize: 'var(--fs-300)',
  },
  track: {
    position: 'relative',
    width: '100%',
    height: '10px',
    background: 'var(--bg-overlay)',
    borderRadius: 'var(--radius-circular)',
    overflow: 'hidden',
  },
  fill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    borderRadius: 'var(--radius-circular)',
    transition: 'width 0.4s ease',
  },
  breakdown: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--space-m)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 'var(--fs-300)',
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    fontSize: 'var(--fs-100)',
    fontFamily: 'var(--font-mono)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--text-tertiary)',
    borderBottom: '1px solid var(--border-default)',
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid var(--border-subtle)',
    verticalAlign: 'middle',
  },
  tdRight: { textAlign: 'right', whiteSpace: 'nowrap' },
  typeTag: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--radius-circular)',
    fontSize: 'var(--fs-100)',
    background: 'var(--accent-weak)',
    color: 'var(--accent)',
    whiteSpace: 'nowrap',
  },
  empty: {
    textAlign: 'center',
    color: 'var(--text-tertiary)',
    padding: 'var(--space-xxl)',
  },
  actions: {
    display: 'flex',
    gap: '4px',
    justifyContent: 'flex-end',
  },
});

const TYPE_LABELS: Record<StorageFileType, string> = {
  project_file: 'Subido a obra',
  quote_excel: 'Cotización generada',
  custom_db: 'BD personalizada',
  catalog: 'Catálogo',
  export: 'Para OPUS',
};

const EXPORT_KIND_LABELS: Record<string, string> = {
  opus_json: 'Export OPUS (JSON)',
  resumen_pdf: 'Resumen ejecutivo (PDF)',
};

export function FilesPage() {
  const styles = useStyles();
  const [status, setStatus] = useState<StorageStatusResponse | null>(null);
  const [files, setFiles] = useState<StorageFile[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([getStorageStatus(), listStorageFiles()]);
      setStatus(s);
      setFiles(f);
      setError(null);
    } catch (e) {
      setError((e as Error).message || 'No se pudo cargar el almacenamiento');
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const visible = files ?? [];

  async function onDelete(f: StorageFile) {
    const msg =
      f.type === 'export'
        ? `¿Borrar "${f.name}" de la carpeta? Libera espacio pero OPUS 27 ya no podrá descargarlo.`
        : f.type === 'catalog'
          ? `¿Borrar el catálogo "${f.name}"? Las cotizaciones que lo usaban pierden esa fuente de precios.`
          : f.type === 'quote_excel'
            ? `¿Borrar el archivo generado de "${f.name}"? La cotización se conserva y podrás volver a generarlo.`
            : `¿Borrar "${f.name}"? Esta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;
    setBusyId(f.id);
    try {
      if (f.type === 'export') await deleteStorageExport(f.id);
      else if (f.type === 'project_file' && f.projectId) await deleteProjectFile(f.projectId, f.id);
      else if (f.type === 'custom_db') await deleteCustomDb(f.id);
      else if (f.type === 'catalog') await deleteCatalog(f.id);
      else if (f.type === 'quote_excel') await deleteQuoteExcel(f.id);
      await reload();
    } catch (e) {
      setError((e as Error).message || 'No se pudo borrar');
    } finally {
      setBusyId(null);
    }
  }

  const pct = status && status.quota_bytes > 0
    ? Math.min(100, Math.round((status.used_bytes / status.quota_bytes) * 100))
    : 0;
  const fillColor = pct >= 95 ? 'var(--bad)' : pct >= 80 ? 'var(--warn)' : 'var(--ok)';

  return (
    <>
      <AppTopBar />
      <Breadcrumbs items={[{ label: 'Inicio', to: '/projects' }, { label: 'Mis archivos' }]} />
      <main className={styles.page}>
        <div>
          <h1 className={styles.title}>Mis archivos</h1>
          <p className={styles.subtitle}>
            Todo lo que ocupa tu almacenamiento: archivos subidos y generados. La carpeta
            “Para OPUS” es la que OPUS 27 puede descargar con tu llave Ecosoft.
          </p>
        </div>

        {error && <p style={{ color: 'var(--bad)', margin: 0 }}>{error}</p>}

        {status && (
          <section className={styles.usageCard} aria-label="Uso de almacenamiento">
            <div className={styles.usageHeader}>
              <span className={styles.usageValue}>
                {formatBytes(status.used_bytes)}{' '}
                <span className={styles.usageQuota}>de {formatBytes(status.quota_bytes)}</span>
              </span>
              <span className={styles.usageQuota}>
                {status.scope === 'tenant'
                  ? `Espacio del equipo (${status.members} miembro${status.members === 1 ? '' : 's'})`
                  : 'Espacio personal'} · {pct}%
              </span>
            </div>
            <div className={styles.track}>
              <div className={styles.fill} style={{ width: `${pct}%`, background: fillColor }} />
            </div>
            <div className={styles.breakdown}>
              <span>Subidos a obras: <strong>{formatBytes(status.breakdown.project_files)}</strong></span>
              <span>Cotizaciones: <strong>{formatBytes(status.breakdown.quote_excels)}</strong></span>
              <span>Carpeta Para OPUS: <strong>{formatBytes(status.breakdown.exports)}</strong></span>
              <span>BDs: <strong>{formatBytes(status.breakdown.custom_dbs)}</strong></span>
              <span>Catálogos: <strong>{formatBytes(status.breakdown.catalogs)}</strong></span>
            </div>
          </section>
        )}

        {!files ? (
          <div className={styles.empty}><Spinner label="Cargando archivos…" /></div>
        ) : visible.length === 0 ? (
          <div className={styles.empty}>
            Aún no hay archivos. Sube archivos a una obra o genera una cotización y aparecerán aquí.
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Archivo</th>
                <th className={styles.th}>Tipo</th>
                <th className={styles.th}>Obra</th>
                <th className={mergeClasses(styles.th, styles.tdRight)}>Tamaño</th>
                <th className={mergeClasses(styles.th, styles.tdRight)}>Fecha</th>
                <th className={mergeClasses(styles.th, styles.tdRight)}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((f) => (
                <tr key={`${f.type}-${f.id}`}>
                  <td className={styles.td}>{f.name}</td>
                  <td className={styles.td}>
                    <span className={styles.typeTag}>
                      {f.type === 'export' && f.kind ? EXPORT_KIND_LABELS[f.kind] ?? TYPE_LABELS.export : TYPE_LABELS[f.type]}
                    </span>
                  </td>
                  <td className={styles.td}>
                    {f.projectId ? (
                      <Link to={`/project/${f.projectId}`} style={{ color: 'var(--accent)' }}>
                        {f.projectName ?? 'Ver obra'}
                      </Link>
                    ) : '—'}
                  </td>
                  <td className={mergeClasses(styles.td, styles.tdRight)}>{formatBytes(f.sizeBytes)}</td>
                  <td className={mergeClasses(styles.td, styles.tdRight)}>
                    {f.at ? new Date(f.at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td className={mergeClasses(styles.td, styles.tdRight)}>
                    <div className={styles.actions}>
                      {f.type === 'export' && (
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<ArrowDownload20Regular />}
                          title="Descargar"
                          onClick={() => window.open(exportDownloadUrl(f.id), '_blank')}
                        />
                      )}
                      {/* Mensajes (docs/29): el PDF del resumen va adjunto; la cotización, como enlace. */}
                      {f.type === 'export' && f.kind === 'resumen_pdf' && (
                        <BotonesMensajes
                          soloIconos
                          appearance="subtle"
                          app={brandingProductoActivo().producto}
                          refDoc={f.id}
                          titulo={f.name}
                          url={`${window.location.origin}${f.projectId ? `/project/${f.projectId}` : '/files'}`}
                          mensaje={mensajeDelResumen(f.projectName)}
                          cargarAdjunto={() => descargarParaAdjuntar(exportDownloadUrl(f.id), f.name)}
                        />
                      )}
                      {f.type === 'quote_excel' && f.projectId && f.quoteId && (
                        <BotonesMensajes
                          soloIconos
                          appearance="subtle"
                          app={brandingProductoActivo().producto}
                          refDoc={f.quoteId}
                          titulo={`Presupuesto ${f.name}`}
                          url={`${window.location.origin}/project/${f.projectId}/quotes/${f.quoteId}`}
                          mensaje={mensajeDelPresupuesto(f.name, `${window.location.origin}/project/${f.projectId}/quotes/${f.quoteId}`)}
                        />
                      )}
                      {f.type === 'project_file' && f.projectId && (
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<Open20Regular />}
                          title="Administrar en la obra"
                          onClick={() => window.location.assign(`/project/${f.projectId}`)}
                        />
                      )}
                      {f.type === 'custom_db' && (
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<Open20Regular />}
                          title="Administrar en Mis BDs"
                          onClick={() => window.location.assign('/custom-dbs')}
                        />
                      )}
                      {f.type === 'catalog' && (
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<Open20Regular />}
                          title="Administrar en Catálogos"
                          onClick={() => window.location.assign(`/catalogs/${f.id}`)}
                        />
                      )}
                      {f.type === 'quote_excel' && f.projectId && f.quoteId && (
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<Open20Regular />}
                          title="Ver cotización"
                          onClick={() => window.location.assign(`/project/${f.projectId}/quotes/${f.quoteId}`)}
                        />
                      )}
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Delete20Regular />}
                        title={f.type === 'export' ? 'Borrar (libera espacio)' : 'Borrar'}
                        disabled={busyId === f.id}
                        onClick={() => void onDelete(f)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>
    </>
  );
}
